import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/StatCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import Upsell from "./Upsell";
import Wizard from "./Wizard";
import Briefing from "./Briefing";
import Tasks from "./Tasks";
import Agenda from "./Agenda";
import Chat from "./Chat";
import SettingsPanel from "./SettingsPanel";
import Automation from "./Automation";

function StatusBadge({ status, t }) {
  if (!status) return null;
  const date = (status.current_period_end || "").slice(0, 10);
  const map = {
    trialing: { cls: "border-amber-200 bg-amber-50 text-amber-700", label: status.trial_end ? t("aipa.trial_until", { date: (status.trial_end || "").slice(0, 10) }) : t("aipa.status_trial") },
    active: { cls: "border-emerald-200 bg-emerald-50 text-emerald-700", label: status.cancel_at_period_end ? t("aipa.canceled_until", { date }) : t("aipa.status_active") },
    canceled: { cls: "border-rose-200 bg-rose-50 text-rose-700", label: t("aipa.canceled_until", { date }) },
  };
  const m = map[status.status] || map.active;
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${m.cls}`} data-testid="aipa-status-badge">{m.label}</span>;
}

export default function AiAssistant() {
  const { user } = useAuth();
  const t = useT();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/coach/assistant/status"); setStatus(data); }
    catch (err) { toast.error(apiError(err, t("aipa.checkout_error"))); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (user && user.role !== "coach") return;
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    (async () => {
      if (checkout === "success" && sessionId) {
        try { await api.get(`/coach/assistant/confirm?session_id=${sessionId}`); toast.success(t("aipa.status_active")); } catch { /* webhook will catch up */ }
      }
      if (checkout) { params.delete("checkout"); params.delete("session_id"); setParams(params, { replace: true }); }
      await loadStatus();
    })();
  }, [user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;
  if (loading || !status) return <div className="space-y-4"><Skeleton className="h-20 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>;

  if (!status.active) return <div data-testid="aipa-page"><Seo title={t("aipa.seo")} noindex /><Upsell status={status} /></div>;
  if (!status.setup_done) return <div data-testid="aipa-page"><Seo title={t("aipa.seo")} noindex /><Wizard onDone={loadStatus} /></div>;

  return (
    <div data-testid="aipa-page">
      <Seo title={t("aipa.seo")} noindex />
      <PageHeader eyebrow={t("aipa.title")} title={t("aipa.title")} subtitle={t("aipa.subtitle")} action={<StatusBadge status={status} t={t} />} />
      <Tabs defaultValue="briefing" className="w-full">
        <TabsList className="mb-4 flex flex-wrap">
          <TabsTrigger value="briefing" data-testid="aipa-tab-briefing">{t("aipa.tab_briefing")}</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="aipa-tab-tasks">{t("aipa.tab_tasks")}</TabsTrigger>
          <TabsTrigger value="automation" data-testid="aipa-tab-automation">{t("aipa.tab_automation")}</TabsTrigger>
          <TabsTrigger value="agenda" data-testid="aipa-tab-agenda">{t("aipa.tab_agenda")}</TabsTrigger>
          <TabsTrigger value="chat" data-testid="aipa-tab-chat">{t("aipa.tab_chat")}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="aipa-tab-settings">{t("aipa.tab_settings")}</TabsTrigger>
        </TabsList>
        <TabsContent value="briefing"><Briefing /></TabsContent>
        <TabsContent value="tasks"><Tasks /></TabsContent>
        <TabsContent value="automation"><Automation /></TabsContent>
        <TabsContent value="agenda"><Agenda /></TabsContent>
        <TabsContent value="chat"><Chat /></TabsContent>
        <TabsContent value="settings"><SettingsPanel status={status} onChange={loadStatus} /></TabsContent>
      </Tabs>
    </div>
  );
}
